import axios from 'axios';
import { API_BASE_URL, buildApiUrl } from '../../config/env';
import {
  DriverAccountType,
  DriverAuthResponse,
  DriverBankDetailsRequest,
  DriverCompanyDetailsRequest,
  DriverSignUpRequest,
  DriverUser,
  DriverVehicleDetailsRequest,
} from '../../types/driverAuth';
import { sanitizeApiErrorMessage } from './errorMessages';
import { clearDriverAuthFlow } from '../storage/driverFlow';
import { getWebSessionContext } from '../storage/authTokens';
import { attachRoleAuthInterceptors } from './authInterceptors';
import { logoutRoleSession, normalizeTokenBundle, refreshRoleSession } from './tokenRefresh';

const api = axios.create({
  baseURL: API_BASE_URL,
});

const publicApi = axios.create({
  baseURL: API_BASE_URL,
});

const openEndpoints = [
  '/driver-auth-service/v2/auth/create',
  '/driver-auth-service/v2/auth/verify-email',
  '/driver-auth-service/v2/auth/login',
  '/driver-auth-service/v2/auth/resend-email-otp',
  '/driver-auth-service/v2/auth/oauth2/authorize',
  '/driver-auth-service/oauth2',
];

export function driverEndpointNeedsAuth(requestUrl: string): boolean {
  return !openEndpoints.some((endpoint) => requestUrl.startsWith(endpoint));
}

attachRoleAuthInterceptors(api, {
  role: 'driver',
  endpointNeedsAuth: driverEndpointNeedsAuth,
  clearSession: clearDriverAuthFlow,
});

export function normalizeDriverApiErrorMessage(rawMessage: string, fallbackMessage: string): string {
  const message = rawMessage.trim();

  if (!message) {
    return fallbackMessage;
  }

  if (
    /email is already in use!?/i.test(message) ||
    /driver information already completed/i.test(message)
  ) {
    return 'This email is already registered as a driver. Try logging in instead.';
  }

  if (
    /driver_phone_key/i.test(message) ||
    /duplicate key value violates unique constraint.*phone/i.test(message) ||
    /phone.*already (exists|in use|registered)/i.test(message)
  ) {
    return 'This phone number is already linked to another driver account.';
  }

  if (/network error/i.test(message)) {
    return 'We could not reach the ShiftWagon server right now. Please try again.';
  }

  if (/refreshToken is required|refresh_token is required/i.test(message)) {
    return 'Your driver session needs to be refreshed. Please log in again and continue onboarding.';
  }

  return sanitizeApiErrorMessage(message, fallbackMessage);
}

export function mapDriverApiStatusMessage(status?: number): string | null {
  if (status === 409) {
    return 'This email is already registered as a driver. Try logging in instead.';
  }

  return null;
}

function extractErrorMessage(error: unknown, fallbackMessage: string): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const responseData = error.response?.data;
    const mappedStatusMessage = mapDriverApiStatusMessage(status);

    if (mappedStatusMessage) {
      return mappedStatusMessage;
    }

    if (typeof responseData === 'string' && responseData.trim()) {
      return normalizeDriverApiErrorMessage(responseData, fallbackMessage);
    }

    if (responseData && typeof responseData === 'object') {
      const data = responseData as Record<string, unknown>;
      const candidate =
        (typeof data.message === 'string' && data.message) ||
        (typeof data.error === 'string' && data.error) ||
        (typeof data.details === 'string' && data.details);

      if (candidate) {
        return normalizeDriverApiErrorMessage(candidate, fallbackMessage);
      }
    }

    if (error.code === 'ERR_NETWORK' || /network error/i.test(error.message || '')) {
      return 'We could not reach the ShiftWagon server right now. Please try again.';
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return normalizeDriverApiErrorMessage(error.message, fallbackMessage);
  }

  return fallbackMessage;
}

function mapDriver(user: any): DriverUser {
  const accountType =
    user.accountType ??
    user.account_type ??
    (user.companyDetails?.companyName || user.company_details?.companyName
      ? 'COMPANY_DRIVER'
      : user.bankDetails?.bankName ||
          user.bank_details?.bankName ||
          user.attachments?.BANK_LETTER
        ? 'INDIVIDUAL_OWNER'
        : null);

  return {
    id: String(user.id),
    name: user.name,
    email: user.email,
    roles: user.roles ?? ['ROLE_DRIVER'],
    phoneE164: user.phoneE164,
    emailVerified: user.emailVerified,
    phoneVerified: user.phoneVerified,
    googleLinked: user.googleLinked,
    onboardingsStep: user.onboardingsStep ?? user.onboardings_step ?? user.onboardingsstep,
    homeBaseLat: user.homeBaseLat,
    homeBaseLng: user.homeBaseLng,
    homeBaseTown: user.homeBaseTown,
    homeBaseProvince: user.homeBaseProvince,
    homeBaseCountry: user.homeBaseCountry,
    accountType,
    bankDetails: user.bankDetails ?? user.bank_details ?? null,
    companyDetails: user.companyDetails ?? user.company_details ?? null,
    vehicles: user.vehicles ?? user.vehicleDetails ?? null,
    attachments: user.attachments ?? {},
  };
}

function mapAuthResponse(response: any): DriverAuthResponse {
  const message = String(response.message ?? '');
  const tokenBundle = normalizeTokenBundle(response);
  const hasDriver = Boolean(response.driverResponse || response.driver || response.driver_response);
  const inferredSuccess =
    typeof response.success === 'boolean'
      ? response.success
      : hasDriver || /driver created successfully|email successfully verified|verified successfully/i.test(message);

  return {
    success: inferredSuccess,
    message,
    token: tokenBundle.accessToken,
    accessToken: tokenBundle.accessToken,
    refreshToken: tokenBundle.refreshToken,
    accessTokenExpiresAt: tokenBundle.accessTokenExpiresAt,
    refreshTokenExpiresAt: tokenBundle.refreshTokenExpiresAt,
    require2FA: Boolean(response.require2FA),
    driverResponse: response.driverResponse
      ? mapDriver(response.driverResponse)
      : response.driver
        ? mapDriver(response.driver)
        : response.driver_response
          ? mapDriver(response.driver_response)
          : undefined,
  };
}

export function getDriverGoogleSignUpUrl(): string {
  return buildApiUrl('driver-auth-service/v2/auth/oauth2/authorize/google?client=web');
}

export async function driverSignUp(payload: DriverSignUpRequest): Promise<DriverAuthResponse> {
  try {
    const response = await publicApi.post('/driver-auth-service/v2/auth/create', payload);
    return mapAuthResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not create driver account.'));
  }
}

export async function driverSignIn(payload: {
  email: string;
  password: string;
}): Promise<DriverAuthResponse> {
  try {
    const response = await publicApi.post('/driver-auth-service/v2/auth/login', {
      ...payload,
      ...getWebSessionContext(),
    });
    return mapAuthResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not sign in.'));
  }
}

export async function requestDriverPasswordReset(email: string): Promise<{
  challengeId: string;
  message: string;
}> {
  try {
    const response = await publicApi.post('/driver-management/api/drivers/forgot-password', { email });
    return {
      challengeId: String(response.data?.challengeId ?? ''),
      message: response.data?.message ?? 'If the account exists, a password reset code has been sent.',
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to send password reset code. Please try again.'));
  }
}

export async function resetDriverPassword(payload: {
  challengeId: string;
  email: string;
  code: string;
  newPassword: string;
}): Promise<void> {
  try {
    await publicApi.post('/driver-management/api/drivers/reset-password', payload);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to reset password. Please check your code and try again.'));
  }
}

export async function verifyDriverEmail(email: string, code: string): Promise<DriverAuthResponse> {
  try {
    const response = await publicApi.post('/driver-auth-service/v2/auth/verify-email', {
      email,
      code,
      ...getWebSessionContext(),
    });
    return mapAuthResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Email verification failed.'));
  }
}

export async function refreshDriverAuthSession(): Promise<string | null> {
  return refreshRoleSession('driver');
}

export async function logoutDriverAuthSession(): Promise<void> {
  await logoutRoleSession('driver');
  clearDriverAuthFlow();
}

export async function resendDriverEmailOtp(email: string): Promise<void> {
  try {
    await publicApi.post('/driver-auth-service/v2/auth/resend-email-otp', { email });
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not resend the email code.'));
  }
}

export async function fetchDriverByEmail(email: string, token?: string): Promise<DriverUser> {
  try {
    const response = await api.get(`/driver-management/api/drivers/byEmail`, {
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : undefined,
      params: { email },
    });
    return mapDriver(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load driver details.'));
  }
}

export async function sendDriverPhoneOtp(driverId: string, phoneE164: string): Promise<void> {
  try {
    await api.post('/driver-management/api/driver-auth/phone/send-otp', {
      driverId,
      phoneE164,
    });
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not send the phone verification code.'));
  }
}

export async function verifyDriverPhoneOtp(
  driverId: string,
  phoneE164: string,
  code: string,
): Promise<DriverUser> {
  try {
    const response = await api.post('/driver-management/api/driver-auth/phone/verify-otp', {
      driverId,
      phoneE164,
      code,
    });

    return mapDriver(response.data.driver ?? response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not verify your phone number.'));
  }
}

export async function resendDriverPhoneOtp(email: string, phone: string): Promise<void> {
  try {
    await api.post('/driver-auth-service/v2/auth/resend-phone-otp', { email, phone });
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not resend the phone code.'));
  }
}

export async function uploadDriverFaceLiveness(
  driverId: string,
  challengeType: 'STRAIGHT' | 'LEFT' | 'RIGHT',
  sequenceOrder: 1 | 2 | 3,
  file: Blob,
): Promise<DriverUser> {
  const formData = new FormData();
  formData.append('driverId', driverId);
  formData.append('challengeType', challengeType);
  formData.append('sequenceOrder', String(sequenceOrder));
  formData.append('file', file, `face_liveness_${challengeType.toLowerCase()}_${Date.now()}.jpg`);

  try {
    const response = await api.post('/driver-management/api/drivers/onboarding/files/face-liveness', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000,
    });

    return mapDriver(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not upload face verification images.'));
  }
}

async function uploadDriverFile(
  endpoint: string,
  driverId: string,
  file: File,
  params?: Record<string, string>,
): Promise<DriverUser> {
  const formData = new FormData();
  formData.append('driverId', driverId);
  formData.append('file', file, file.name);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        formData.append(key, value);
      }
    });
  }

  try {
    const response = await api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000,
    });

    return mapDriver(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not upload the selected file.'));
  }
}

export async function uploadDriverIdCopy(driverId: string, file: File): Promise<DriverUser> {
  return uploadDriverFile('/driver-management/api/drivers/onboarding/files/id-copy', driverId, file);
}

export async function uploadDriverCriminalRecord(
  driverId: string,
  file: File,
  issuedOn?: string,
): Promise<DriverUser> {
  return uploadDriverFile(
    '/driver-management/api/drivers/onboarding/files/criminal-record',
    driverId,
    file,
    issuedOn ? { issuedOn } : undefined,
  );
}

export async function uploadDriverPhoto(driverId: string, file: File): Promise<DriverUser> {
  return uploadDriverFile('/driver-management/api/drivers/onboarding/files/driver-photo', driverId, file);
}

export async function completeDriverPersonalDocs(driverId: string): Promise<DriverUser> {
  try {
    const response = await api.post('/driver-management/api/drivers/personal-docs/complete', null, {
      params: { driverId },
    });
    return mapDriver(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not complete personal documents.'));
  }
}

export async function submitDriverVehicleDetails(
  driverId: string,
  payload: DriverVehicleDetailsRequest,
): Promise<DriverUser> {
  try {
    const response = await api.post(`/driver-management/api/drivers/${driverId}/vehicle-details`, payload);
    return mapDriver(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not save vehicle details.'));
  }
}

export async function uploadDriverVehiclePhoto(
  driverId: string,
  angle: 'FRONT' | 'LEFT' | 'RIGHT' | 'BACK',
  file: File,
): Promise<DriverUser> {
  return uploadDriverFile(
    '/driver-management/api/drivers/onboarding/files/vehicle-photo',
    driverId,
    file,
    { angle },
  );
}

export async function uploadDriverRoadworthy(
  driverId: string,
  file: File,
  issuedOn: string,
  validTo: string,
): Promise<DriverUser> {
  return uploadDriverFile('/driver-management/api/drivers/onboarding/files/roadworthy', driverId, file, {
    issuedOn,
    validTo,
  });
}

export async function uploadDriverLicenceDisk(
  driverId: string,
  file: File,
  issuedOn: string,
  validTo: string,
): Promise<DriverUser> {
  return uploadDriverFile(
    '/driver-management/api/drivers/onboarding/files/licence-disk',
    driverId,
    file,
    { issuedOn, validTo },
  );
}

export async function uploadDriverPdp(
  driverId: string,
  file: File,
  issuedOn: string,
  validTo: string,
): Promise<DriverUser> {
  return uploadDriverFile('/driver-management/api/drivers/onboarding/files/pdp', driverId, file, {
    issuedOn,
    validTo,
  });
}

export async function uploadDriverInsuranceLetter(driverId: string, file: File): Promise<DriverUser> {
  return uploadDriverFile(
    '/driver-management/api/drivers/onboarding/files/insurance-letter',
    driverId,
    file,
  );
}

export async function completeDriverVehicleInformation(driverId: string): Promise<DriverUser> {
  try {
    const response = await api.post('/driver-management/api/drivers/vehicle-info/complete', null, {
      params: { driverId },
    });
    return mapDriver(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not complete vehicle information.'));
  }
}

export async function setDriverAccountType(
  driverId: string,
  type: DriverAccountType,
): Promise<DriverUser> {
  try {
    const response = await api.post(`/driver-management/api/drivers/${driverId}/account-type`, { type });
    return mapDriver(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not save account type.'));
  }
}

export async function submitDriverCompanyDetails(
  driverId: string,
  payload: DriverCompanyDetailsRequest,
): Promise<DriverUser> {
  try {
    const response = await api.post(`/driver-management/api/drivers/${driverId}/company-details`, payload);
    return mapDriver(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not save company details.'));
  }
}

export async function uploadDriverBankLetter(driverId: string, file: File): Promise<DriverUser> {
  return uploadDriverFile('/driver-management/api/drivers/onboarding/files/bank-letter', driverId, file);
}

export async function submitDriverBankDetails(
  driverId: string,
  payload: DriverBankDetailsRequest,
): Promise<DriverUser> {
  try {
    const response = await api.post(`/driver-management/api/drivers/${driverId}/bank-details`, payload);
    return mapDriver(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not save bank details.'));
  }
}

export async function submitDriverOnboarding(driverId: string): Promise<DriverUser> {
  try {
    const response = await api.post(`/driver-management/api/drivers/${driverId}/submit`);
    return mapDriver(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not submit driver onboarding.'));
  }
}
